import type { FC, ReactNode } from 'react'
import type { Stat } from '@/constants/site'

import { useRef } from 'react'
import { cn } from '@/lib/utils'
import { gsap, MOTION, useGSAP } from '@/lib/motion'
import { SplitWords } from '@/components/generals/split-words'
import { STATS } from '@/constants/site'

const DIGITS = Array.from({ length: 20 }, (_, index) => index % 10)

/** Two full turns of 0 to 9 per column, so every digit rolls before it settles. */
const Odometer: FC<Stat> = ({ value, suffix }): ReactNode => {
    return (
        <span aria-hidden='true' className='inline-flex items-start tabular-nums'>
            {value.split('').map((char, index) => {
                if (!/\d/.test(char)) {
                    return (
                        <span key={`${char}-${index}`} className='inline-block h-[0.86em] leading-[0.86]'>
                            {char}
                        </span>
                    )
                }

                const digit = Number(char)

                return (
                    <span key={`${char}-${index}`} className='relative inline-block h-[0.86em] overflow-hidden'>
                        <span
                            data-digit={digit}
                            className='flex flex-col'
                            style={{ transform: `translateY(${-((10 + digit) / 20) * 100}%)` }}
                        >
                            {DIGITS.map((item, position) => (
                                <span key={position} className='block h-[0.86em] leading-[0.86]'>
                                    {item}
                                </span>
                            ))}
                        </span>
                    </span>
                )
            })}

            {suffix && <span className='inline-block h-[0.86em] font-light leading-[0.86]'>{suffix}</span>}
        </span>
    )
}

const LAYOUTS = [
    { number: 'md:col-span-8 md:col-start-1 md:row-start-1', label: 'md:col-span-4 md:col-start-9 md:row-start-1 md:text-right' },
    { number: 'md:col-span-8 md:col-start-5 md:row-start-1 md:text-right', label: 'md:col-span-4 md:col-start-1 md:row-start-1' },
    { number: 'md:col-span-7 md:col-start-3 md:row-start-1', label: 'md:col-span-3 md:col-start-10 md:row-start-1 md:text-right' }
]

/** Chapter seven, in full cyan: thank-you, with the numbers rolling into place like a counter. */
export const GratitudeSection: FC = (): ReactNode => {
    const ref = useRef<HTMLElement>(null)

    useGSAP(() => {
        const section = ref.current

        if (!section) return

        const q = gsap.utils.selector(section)
        const mm = gsap.matchMedia()

        mm.add(MOTION.ok, () => {
            gsap.fromTo(q('[data-title] [data-word]'), { yPercent: 110 }, {
                yPercent: 0,
                duration: 1.1,
                stagger: 0.06,
                ease: 'expo.out',
                scrollTrigger: { trigger: q('[data-title]')[0], start: 'top 82%', toggleActions: 'play none none reverse' }
            })

            q('[data-stat]').forEach(row => {
                const columns = row.querySelectorAll<HTMLElement>('[data-digit]')

                gsap.set(columns, { clearProps: 'transform' })

                gsap.timeline({
                    scrollTrigger: { trigger: row, start: 'top 92%', end: 'top 40%', scrub: 0.6 }
                })
                    .fromTo(row.querySelector('[data-rule]'), { scaleX: 0 }, { scaleX: 1, ease: 'none', duration: 0.5 }, 0)
                    .fromTo(columns, { yPercent: 0 }, {
                        yPercent: (_, target: HTMLElement) => -((10 + Number(target.dataset.digit)) / 20) * 100,
                        ease: 'power2.out',
                        duration: 1,
                        stagger: 0.12
                    }, 0)
                    .fromTo(row.querySelector('[data-stat-label]'), { yPercent: 100, autoAlpha: 0 }, { yPercent: 0, autoAlpha: 1, ease: 'power2.out', duration: 0.4 }, 0.5)
            })
        })

        return () => mm.revert()
    }, { scope: ref })

    return (
        <section
            ref={ref}
            id='thank-you'
            data-tone='signal'
            data-surface='light'
            aria-labelledby='thank-you-title'
            className='relative bg-signal px-(--gutter) pb-[12vh] pt-[18vh] text-ink'
        >
            <div className='grid gap-x-8 gap-y-8 md:grid-cols-12'>
                <p className='label md:col-span-3 md:pt-4'>
                    <span className='mr-3'>05</span>
                    Thank you
                </p>

                <div className='md:col-span-9'>
                    <h2
                        id='thank-you-title'
                        data-title=''
                        className='display text-[clamp(2.8rem,7vw,8.25rem)] leading-[0.88] wdth-87'
                    >
                        <SplitWords text="We are grateful." />
                    </h2>

                    <p className='mt-8 max-w-[46ch] text-lg leading-relaxed md:text-xl'>
                        Thank you to our many amazing partners. We love working with you! You have been a tremendous blessing to us.
                        Here&apos;s a quick picture of what we&apos;ve accomplished together.
                    </p>
                </div>
            </div>

            <dl className='mt-[12vh]'>
                {STATS.map((stat, index) => {
                    const layout = LAYOUTS[index % LAYOUTS.length]

                    return (
                        <div key={stat.label} data-stat='' className='relative grid items-end gap-x-8 gap-y-2 py-[3vh] md:grid-cols-12'>
                            <dt className={cn('order-2 overflow-hidden md:order-none md:pb-[2.2vw]', layout.label)}>
                                <span data-stat-label='' className='label block text-[0.78rem]'>{stat.label}</span>
                            </dt>

                            <dd className={cn('display text-[clamp(6.5rem,23vw,25rem)] leading-[0.86] wdth-62', layout.number)}>
                                <span data-rule='' aria-hidden='true' className='absolute left-0 top-0 h-px w-full origin-left bg-ink/30' />
                                <span className='sr-only'>{stat.value}{stat.suffix ?? ''}</span>
                                <Odometer {...stat} />
                            </dd>
                        </div>
                    )
                })}
            </dl>
        </section>
    )
}
